import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Heart } from "lucide-react";
import type { Property } from "@/types/property";
import {
  formatArea,
  formatBathrooms,
  formatBedrooms,
  formatListingTitle,
  formatPrice,
} from "@/lib/format";
import { cn } from "@/lib/utils";
import { rankPropertyImages } from "@/lib/property-images";
import { propertyPath } from "@/lib/property-url";
import { SafeImage } from "./SafeImage";
import { useCompare } from "@/lib/compare-context";
import { useSiteConfig, whatsappHref } from "@/lib/site-config";

interface PropertyCardProps {
  property: Property;
  variant?: "default" | "feature";
  className?: string;
  coverOffset?: number;
}

/**
 * Enterprise property card. Photograph leads, but the metadata frame
 * is now structured: badge top-left of image, community over title,
 * spec row + price split across a hairline at the bottom.
 *
 * Per the June 2026 feedback round:
 *   - All listing badges (For Sale / For Rent / Off-Plan) are white.
 *   - The photo is a 3-image slider; the card links to the full listing.
 *   - The corner actions are a "Save to shortlist" heart and an
 *     always-visible WhatsApp button, pre-filled with the listing.
 *   - The visible title follows `Key Feature · Community · Property Type`.
 */
export function PropertyCard({
  property,
  variant = "default",
  className,
  coverOffset = 0,
}: PropertyCardProps) {
  const isFeature = variant === "feature";
  const compare = useCompare();
  const isShortlisted = compare.has(property.id);
  const siteConfig = useSiteConfig();

  const labelTag = property.isExclusive
    ? "Exclusive"
    : property.listingType === "off-plan"
      ? "Off-Plan"
      : property.listingType === "rent"
        ? "For Rent"
        : "For Sale";

  const badgeStyle = badgeStyleFor(property);
  const presentationTitle = formatListingTitle(property);
  const displayImages = rankPropertyImages(property.images, property.id, {
    coverOffset,
  });
  // Card slider: up to 3 photos; the card itself links through to the
  // full gallery and details.
  const slides = displayImages.slice(0, 3);
  const [slide, setSlide] = useState(0);
  const goTo = (e: React.MouseEvent, next: number) => {
    e.preventDefault();
    e.stopPropagation();
    setSlide(((next % slides.length) + slides.length) % slides.length);
  };

  const whatsappMessage = encodeURIComponent(
    `Hi Hive Homes, I'd like to enquire about ${presentationTitle}.`,
  );
  const whatsappLink = siteConfig.whatsapp.set
    ? `${whatsappHref(siteConfig.whatsapp)}?text=${whatsappMessage}`
    : null;

  return (
    <Link
      to={propertyPath(property)}
      className={cn("group flex h-full flex-col", className)}
    >
      <div
        className={cn(
          "relative overflow-hidden bg-[var(--surface-2)]",
          "aspect-[16/10]",
        )}
      >
        <div
          className="flex h-full w-full transition-transform duration-500 ease-out"
          style={{ transform: `translateX(-${slide * 100}%)` }}
        >
          {slides.map((src, i) => (
            <div key={src + i} className="h-full w-full flex-none">
              <SafeImage
                src={src}
                alt={i === 0 ? presentationTitle : ""}
                loading="lazy"
                sizes={variant === "feature" ? "(min-width: 1024px) 33vw, 100vw" : "(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw"}
                widths={[360, 540, 720, 960, 1280]}
                className="h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]"
              />
            </div>
          ))}
        </div>

        {slides.length > 1 && (
          <>
            <button
              type="button"
              onClick={(e) => goTo(e, slide - 1)}
              aria-label="Previous photo"
              className="card-control card-control-sm absolute left-2 top-1/2 z-[1] -translate-y-1/2 bg-[rgba(13,20,34,0.55)] text-white opacity-90 backdrop-blur-sm transition-opacity hover:bg-[rgba(13,20,34,0.8)] hoverable:sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100"
            >
              <ChevronLeft className="size-5 sm:size-4" aria-hidden />
            </button>
            <button
              type="button"
              onClick={(e) => goTo(e, slide + 1)}
              aria-label="Next photo"
              className="card-control card-control-sm absolute right-2 top-1/2 z-[1] -translate-y-1/2 bg-[rgba(13,20,34,0.55)] text-white opacity-90 backdrop-blur-sm transition-opacity hover:bg-[rgba(13,20,34,0.8)] hoverable:sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100"
            >
              <ChevronRight className="size-5 sm:size-4" aria-hidden />
            </button>
            <span className="pointer-events-none absolute inset-x-0 bottom-2.5 z-[1] flex items-center justify-center gap-1.5">
              {slides.map((_, i) => (
                <span
                  key={i}
                  aria-hidden
                  className={cn(
                    "size-1.5 rounded-full transition-colors",
                    i === slide ? "bg-white" : "bg-white/45",
                  )}
                />
              ))}
            </span>
          </>
        )}

        <span className={badgeStyle}>{labelTag.toUpperCase()}</span>

        {/* Phones: side by side along the top edge, clear of the
            vertically-centred photo arrows. Desktop: the original stack. */}
        <div className="absolute right-2 top-2 z-[1] flex flex-row-reverse items-start gap-2 sm:right-3 sm:top-3 sm:flex-col sm:items-end">
          <button
            type="button"
            aria-pressed={isShortlisted}
            aria-label={
              isShortlisted ? "Remove from shortlist" : "Save to shortlist"
            }
            title={
              isShortlisted ? "Remove from shortlist" : "Save to shortlist"
            }
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const result = compare.toggle(property.id);
              if (result === "limit") {
                alert(
                  `You can shortlist up to ${compare.max} homes at a time.`,
                );
              }
            }}
            className={cn(
              "card-control transition-all",
              isShortlisted
                ? "bg-[var(--cream)] text-[var(--navy)]"
                : "bg-[rgba(13,20,34,0.65)] text-[var(--cream)] backdrop-blur-sm hover:bg-[var(--cream)] hover:text-[var(--navy)]",
            )}
          >
            <Heart
              className={cn("size-[18px] sm:size-4", isShortlisted && "fill-current")}
              strokeWidth={isShortlisted ? 0 : 1.6}
            />
          </button>

          {whatsappLink && (
            // Rendered as a <button> rather than an <a>, because the
            // entire card is wrapped in a react-router <Link> (itself
            // an <a>) and nested anchors are invalid HTML — React 19
            // surfaces this as a hydration error, and the browser
            // routes the click ambiguously. The button stops
            // propagation so the card's navigation never fires, then
            // opens WhatsApp in a new tab with the same noopener
            // semantics as the original anchor.
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                window.open(whatsappLink, "_blank", "noopener,noreferrer");
              }}
              aria-label="Enquire on WhatsApp"
              title="Enquire on WhatsApp"
              className="card-control bg-[#25D366] text-white transition-transform duration-200 hover:scale-105"
            >
              <svg
                viewBox="0 0 24 24"
                className="size-[18px] sm:size-4"
                fill="currentColor"
                aria-hidden
              >
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-1 flex-col">
        <div className="mt-4 flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <p className="break-words text-[11px] uppercase tracking-[0.14em] font-medium text-[var(--text-muted)]">
              {property.community}
              {property.subCommunity ? ` · ${property.subCommunity}` : ""}
            </p>
            {/* Titles may run to two lines; anything longer is clipped
                with an ellipsis so the spec row and price never drift. */}
            <h3
              className={cn(
                "mt-2 line-clamp-2 break-words font-display font-semibold text-[var(--text)] transition-colors group-hover:text-[var(--gold-bright)]",
                isFeature
                  ? "text-[24px] md:text-[32px] leading-[1.1] tracking-[-0.025em]"
                  : "text-[18px] md:text-[20px] leading-[1.2] tracking-[-0.02em]",
              )}
              title={presentationTitle}
            >
              {presentationTitle}
            </h3>
          </div>
        </div>

        <div className="mt-auto pt-4">
          <div className="flex items-baseline justify-between gap-3 border-t border-[var(--line)] pt-3.5">
            <ul className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-[12.5px] text-[var(--text-soft)] sm:gap-x-4">
              <li className="whitespace-nowrap">{formatBedrooms(property.bedrooms)}</li>
              {property.bathrooms > 0 && (
                <>
                  <li aria-hidden className="text-[var(--text-muted)]">·</li>
                  <li className="whitespace-nowrap">{formatBathrooms(property.bathrooms)}</li>
                </>
              )}
              <li aria-hidden className="text-[var(--text-muted)]">·</li>
              <li className="whitespace-nowrap">{formatArea(property.area)}</li>
            </ul>
          </div>

          <p
            className={cn(
              "mt-3 break-words font-display font-semibold text-[var(--gold-bright)] tracking-[-0.025em] tabular-nums",
              isFeature ? "text-[24px] sm:text-[26px]" : "text-[19px] sm:text-[20px]",
            )}
          >
            {formatPrice(property.price, property.pricePeriod)}
          </p>
        </div>
      </div>
    </Link>
  );
}

function badgeStyleFor(_property: Property): string {
  // All listing badges (For Sale / For Rent / Off-Plan) are white with
  // navy text, per the June 2026 feedback round.
  return "absolute left-3 top-3 z-[1] inline-flex items-center bg-white px-2.5 py-1 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-[var(--navy)]";
}
